import React, { FC, useMemo, useState } from 'react';
import { FormControl, TextField, IconButton } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import Autocomplete from '@mui/material/Autocomplete';
import ListView, { ListViewItem } from '@ingka/list-view';
import CloseIcon from '@mui/icons-material/Close';
import { ResponsibleAutocompleteProps } from './ResponsibleAutocompleteProps';
import { filterOptions } from './utils';
import { v4 as uuidv4 } from 'uuid';

export type ExpansionProjectEditor = {
  id: number;
  name: string;
  email: string;
};

const ResponsibleAutocomplete: FC<ResponsibleAutocompleteProps> = ({
  id,
  options,
  label,
  testid,
  required,
}) => {
  const { watch, setValue } = useFormContext();

  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState<any>(null);

  const confidentialUsers = watch('expansionProjectEditors');
  const countryCode = watch('countryCode');

  const usersMap = useMemo(() => {
    const userMap = new Map();
    options.forEach((user) => {
      const searchKey = (user.name + ' ' + user.email).toLowerCase();
      if (
        user?.countries?.includes(countryCode) ||
        user?.countries?.includes('All')
      ) {
        userMap.set(searchKey, user);
      }
    });
    return userMap;
  }, [options, countryCode]);
  const filteredOptions = useMemo(() => {
    const options = filterOptions(usersMap, inputValue);
    if (inputValue !== '' && !options.length) {
      options.push({
        email: inputValue,
        name: 'Add',
      });
    }
    return options;
  }, [inputValue, options, countryCode]);

  const handleSelect = (value: any) => {
    if (!value) return;
    const users = [
      ...((confidentialUsers as unknown as ExpansionProjectEditor[]) || []),
    ];
    const alreadyAdded = users.find((u) => u.email === value.email);
    if (!alreadyAdded) {
      setValue('expansionProjectEditors', [
        ...users,
        { ...value, id: uuidv4() },
      ]);
    }
  };
  const handleRemove = (email) => {
    const users = [
      ...((confidentialUsers as unknown as ExpansionProjectEditor[]) || []),
    ];
    const updatedUsers = users.filter((u) => u.email !== email);
    setValue('expansionProjectEditors', updatedUsers);
  };

  return (
    <>
      <FormControl fullWidth>
        <Autocomplete
          fullWidth
          value={selectedValue}
          inputValue={inputValue}
          onChange={(_, value) => {
            handleSelect(value);
            setSelectedValue(null);
            setInputValue('');
          }}
          id={id}
          options={filteredOptions}
          onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
          getOptionLabel={(option) =>
            option?.name ? `${option.name} - ${option.email}` : option
          }
          data-testid={testid}
          renderOption={(props, option) => (
            <li {...props}>
              {option?.name ? `${option.name} - ${option.email}` : option}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label={required ? label + ' *' : label} />
          )}
        />
      </FormControl>
      {confidentialUsers?.length > 0 && (
        <ListView id="selected-users-list" showDivider size="medium">
          {(confidentialUsers as unknown as ExpansionProjectEditor[]).map(
            (user) => (
              <ListViewItem
                key={user.id}
                title={user.name}
                description={
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>{user.email}</span>
                    <IconButton
                      size="small"
                      aria-label="remove"
                      onClick={() => handleRemove(user.email)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
                }
                avatarProps={{
                  screenReaderText: `Initials of ${user.name}`,
                  size: 'small',
                  text: user.name
                    ?.split(' ')
                    .map((n: string) => n[0])
                    .join('')
                    .toUpperCase(),
                  variant: 'primary',
                  withIcon: false,
                }}
              />
            ),
          )}
        </ListView>
      )}
    </>
  );
};

export default ResponsibleAutocomplete;
